import { create } from 'zustand'
import type { VideoCall } from '@/types'

export interface CallSessionMediaPreferences {
  orderID: string
  localAudioEnabled: boolean
  localVideoEnabled: boolean
  remoteAudioEnabled: boolean
}

export type CallSessionMediaPreferencePatch = Partial<
  Pick<CallSessionMediaPreferences, 'localAudioEnabled' | 'localVideoEnabled' | 'remoteAudioEnabled'>
>

export const defaultCallSessionMediaPreferences = {
  localAudioEnabled: true,
  localVideoEnabled: true,
  remoteAudioEnabled: true,
}

function getCallOrderID(call: VideoCall | null) {
  return call?.order_id || call?.call_id || ''
}

function createDefaultSessionMediaPreferences(orderID: string): CallSessionMediaPreferences {
  return {
    orderID,
    ...defaultCallSessionMediaPreferences,
  }
}

interface CallState {
  currentCall: VideoCall | null
  incomingCall: VideoCall | null
  outgoingCall: VideoCall | null
  lastEndedCall: VideoCall | null
  sessionMediaPreferences: CallSessionMediaPreferences | null
  endingCallOrderID: string | null
  incomingActionOrderID: string | null
  incomingAction: 'accept' | 'reject' | null
  setCurrentCall: (call: VideoCall | null) => void
  setIncomingCall: (call: VideoCall | null) => void
  setOutgoingCall: (call: VideoCall | null) => void
  setLastEndedCall: (call: VideoCall | null) => void
  setSessionMediaPreferences: (orderID: string, patch: CallSessionMediaPreferencePatch) => void
  clearSessionMediaPreferences: (orderID?: string) => void
  setEndingCallOrderID: (orderID: string | null) => void
  setIncomingAction: (orderID: string | null, action: 'accept' | 'reject' | null) => void
  clearCalls: () => void
}

export const useCallStore = create<CallState>((set) => ({
  currentCall: null,
  incomingCall: null,
  outgoingCall: null,
  lastEndedCall: null,
  sessionMediaPreferences: null,
  endingCallOrderID: null,
  incomingActionOrderID: null,
  incomingAction: null,
  setCurrentCall: (currentCall) => set({ currentCall, ...(currentCall ? { lastEndedCall: null } : null) }),
  setIncomingCall: (incomingCall) => set({ incomingCall, ...(incomingCall ? { lastEndedCall: null } : null) }),
  setOutgoingCall: (outgoingCall) => set((state) => {
    const nextOrderID = getCallOrderID(outgoingCall)
    if (!outgoingCall) {
      return {
        outgoingCall,
        sessionMediaPreferences: null,
      }
    }
    return {
      outgoingCall,
      lastEndedCall: null,
      sessionMediaPreferences:
        state.sessionMediaPreferences?.orderID === nextOrderID
          ? state.sessionMediaPreferences
          : createDefaultSessionMediaPreferences(nextOrderID),
    }
  }),
  setLastEndedCall: (lastEndedCall) => set({ lastEndedCall }),
  setSessionMediaPreferences: (orderID, patch) => set((state) => {
    if (!orderID) {
      return {}
    }
    const current =
      state.sessionMediaPreferences?.orderID === orderID
        ? state.sessionMediaPreferences
        : createDefaultSessionMediaPreferences(orderID)
    return {
      sessionMediaPreferences: {
        ...current,
        ...patch,
        orderID,
      },
    }
  }),
  clearSessionMediaPreferences: (orderID) => set((state) => {
    if (orderID && state.sessionMediaPreferences?.orderID !== orderID) {
      return {}
    }
    return { sessionMediaPreferences: null }
  }),
  setEndingCallOrderID: (endingCallOrderID) => set({ endingCallOrderID }),
  setIncomingAction: (incomingActionOrderID, incomingAction) => set({ incomingActionOrderID, incomingAction }),
  clearCalls: () => set({
    currentCall: null,
    incomingCall: null,
    outgoingCall: null,
    sessionMediaPreferences: null,
    endingCallOrderID: null,
  }),
}))
